import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useColorScheme } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { tokens } from '@design';
// ============================================================
// Theme Types
// ============================================================

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeColors {
  // Brand Primary
  primary: string;
  primaryHover: string;
  primaryLight: string;

  // Secondary (Blue - Links, Info)
  secondary: string;
  secondaryHover: string;
  secondaryLight: string;

  // Semantic Colors
  success: string;
  successHover: string;
  successLight: string;

  warning: string;
  warningHover: string;
  warningLight: string;

  danger: string;
  dangerHover: string;
  dangerLight: string;

  info: string;
  infoLight: string;

  // Core Colors
  white: string;
  black: string;

  // Background Colors
  background: string;
  backgroundAlt: string;
  backgroundElevated: string;

  // Card Colors
  card: string;
  cardBorder: string;

  // Border Colors
  border: string;
  borderLight: string;
  inputBorder: string;
  divider: string;

  // Text Colors
  text: string;
  textPrimary: string;
  textSecondary: string;
  textBody: string;
  textMuted: string;
  textLight: string;
  textDisabled: string;
  textInverse: string;

  // Icon Colors
  iconDefault: string;
  iconMuted: string;
  iconLight: string;

  // Neutral Scale
  neutral50: string;
  neutral100: string;
  neutral200: string;
  neutral300: string;
  neutral400: string;
  neutral500: string;
  neutral600: string;
  neutral700: string;
  neutral800: string;
  neutral900: string;

  // Gray Scale
  gray50: string;
  gray100: string;
  gray200: string;
  gray300: string;
  gray400: string;
  gray500: string;
  gray600: string;
  gray700: string;
  gray800: string;
  gray900: string;

  // Slate Scale
  slate100: string;
  slate200: string;
  slate300: string;
  slate400: string;
  slate500: string;
  slate600: string;
  slate700: string;
}

export interface Theme {
  mode: 'light' | 'dark';
  colors: ThemeColors;
}

// ============================================================
// Light Theme Colors
// ============================================================

const lightColors: ThemeColors = {
  // Brand Primary
  primary: tokens.colors.semantic.general["Main Color"],
  primaryHover: '#8A1F28',
  primaryLight: tokens.colors.raw.red["50"],

  // Secondary (Blue - Links, Info)
  secondary: tokens.colors.raw.blue["500"],
  secondaryHover: '#2563EB',
  secondaryLight: '#EFF6FF',

  // Semantic Colors
  success: tokens.colors.raw.green["500"],
  successHover: '#16A34A',
  successLight: '#DCFCE7',

  warning: tokens.colors.raw.yellow["500"],
  warningHover: '#CA8A04',
  warningLight: '#FEF9C3',

  danger: tokens.colors.raw.red["500"],
  dangerHover: tokens.colors.raw.red["600"],
  dangerLight: tokens.colors.raw.red["50"],

  info: tokens.colors.raw.blue["500"],
  infoLight: '#DBEAFE',

  // Core Colors
  white: tokens.colors.raw.white,
  black: '#0A0A0A',

  // Background Colors
  background: '#FAFAFA',
  backgroundAlt: tokens.colors.raw.slate["100"],
  backgroundElevated: tokens.colors.raw.white,

  // Card Colors
  card: tokens.colors.raw.white,
  cardBorder: tokens.colors.raw.gray["200"],

  // Border Colors
  border: '#E5E5E5',
  borderLight: tokens.colors.raw.slate["200"],
  inputBorder: '#D4D4D4',
  divider: tokens.colors.raw.gray["100"],

  // Text Colors
  text: '#171717',
  textPrimary: tokens.colors.raw.gray["900"],
  textSecondary: tokens.colors.semantic.general.foreground,
  textBody: '#525252',
  textMuted: tokens.colors.raw.gray["500"],
  textLight: tokens.colors.raw.gray["400"],
  textDisabled: '#A3A3A3',
  textInverse: tokens.colors.raw.white,

  // Icon Colors
  iconDefault: tokens.colors.semantic.general.foreground,
  iconMuted: tokens.colors.raw.gray["500"],
  iconLight: tokens.colors.raw.gray["400"],

  // Neutral Scale
  neutral50: '#FAFAFA',
  neutral100: '#F5F5F5',
  neutral200: '#E5E5E5',
  neutral300: '#D4D4D4',
  neutral400: '#A3A3A3',
  neutral500: '#737373',
  neutral600: '#525252',
  neutral700: '#404040',
  neutral800: '#262626',
  neutral900: '#171717',

  // Gray Scale
  gray50: tokens.colors.raw.gray["50"],
  gray100: tokens.colors.raw.gray["100"],
  gray200: tokens.colors.raw.gray["200"],
  gray300: tokens.colors.raw.gray["300"],
  gray400: tokens.colors.raw.gray["400"],
  gray500: tokens.colors.raw.gray["500"],
  gray600: '#4B5563',
  gray700: tokens.colors.raw.gray["700"],
  gray800: tokens.colors.raw.gray["800"],
  gray900: tokens.colors.raw.gray["900"],

  // Slate Scale
  slate100: tokens.colors.raw.slate["100"],
  slate200: tokens.colors.raw.slate["200"],
  slate300: tokens.colors.raw.slate["300"],
  slate400: '#94A3B8',
  slate500: tokens.colors.raw.slate["500"],
  slate600: '#475569',
  slate700: tokens.colors.raw.slate["700"],
};

// ============================================================
// Dark Theme Colors
// ============================================================

const darkColors: ThemeColors = {
  // Brand Primary (slightly lighter for dark mode)
  primary: '#D34A54',
  primaryHover: tokens.colors.semantic.general["Main Color"],
  primaryLight: '#3D181C',

  // Secondary (Blue - Links, Info)
  secondary: '#60A5FA',
  secondaryHover: tokens.colors.raw.blue["500"],
  secondaryLight: '#1E3A5F',

  // Semantic Colors
  success: '#4ADE80',
  successHover: tokens.colors.raw.green["500"],
  successLight: '#14532D',

  warning: '#FACC15',
  warningHover: tokens.colors.raw.yellow["500"],
  warningLight: '#422006',

  danger: '#F87171',
  dangerHover: tokens.colors.raw.red["500"],
  dangerLight: '#450A0A',

  info: '#60A5FA',
  infoLight: '#1E3A5F',

  // Core Colors
  white: tokens.colors.raw.white,
  black: '#0A0A0A',

  // Background Colors
  background: '#0F0F0F',
  backgroundAlt: '#171717',
  backgroundElevated: '#1F1F1F',

  // Card Colors
  card: '#1A1A1A',
  cardBorder: '#2D2D2D',

  // Border Colors
  border: '#2D2D2D',
  borderLight: '#3D3D3D',
  inputBorder: '#404040',
  divider: '#262626',

  // Text Colors
  text: '#F5F5F5',
  textPrimary: '#FAFAFA',
  textSecondary: '#E5E5E5',
  textBody: '#D4D4D4',
  textMuted: '#A3A3A3',
  textLight: '#737373',
  textDisabled: '#525252',
  textInverse: '#171717',

  // Icon Colors
  iconDefault: '#E5E5E5',
  iconMuted: '#A3A3A3',
  iconLight: '#737373',

  // Neutral Scale (inverted for dark mode)
  neutral50: '#171717',
  neutral100: '#262626',
  neutral200: '#404040',
  neutral300: '#525252',
  neutral400: '#737373',
  neutral500: '#A3A3A3',
  neutral600: '#D4D4D4',
  neutral700: '#E5E5E5',
  neutral800: '#F5F5F5',
  neutral900: '#FAFAFA',

  // Gray Scale (inverted for dark mode)
  gray50: tokens.colors.raw.gray["900"],
  gray100: tokens.colors.raw.gray["800"],
  gray200: tokens.colors.raw.gray["700"],
  gray300: '#4B5563',
  gray400: tokens.colors.raw.gray["500"],
  gray500: tokens.colors.raw.gray["400"],
  gray600: tokens.colors.raw.gray["300"],
  gray700: tokens.colors.raw.gray["200"],
  gray800: tokens.colors.raw.gray["100"],
  gray900: tokens.colors.raw.gray["50"],

  // Slate Scale (inverted for dark mode)
  slate100: tokens.colors.raw.slate["700"],
  slate200: '#475569',
  slate300: tokens.colors.raw.slate["500"],
  slate400: '#94A3B8',
  slate500: tokens.colors.raw.slate["300"],
  slate600: tokens.colors.raw.slate["200"],
  slate700: tokens.colors.raw.slate["100"],
};

// ============================================================
// Theme Context
// ============================================================

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  isDark: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  colors: ThemeColors;
}

const THEME_STORAGE_KEY = '@sms_theme_mode';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ============================================================
// Theme Provider
// ============================================================

export function ThemeProvider({ children }: { children: ReactNode }) {
  const systemColorScheme = useColorScheme();
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load saved theme preference on mount
  useEffect(() => {
    loadThemePreference();
  }, []);

  const loadThemePreference = async () => {
    try {
      const savedMode = await AsyncStorage.getItem(THEME_STORAGE_KEY);
      if (savedMode && ['light', 'dark', 'system'].includes(savedMode)) {
        setThemeModeState(savedMode as ThemeMode);
      }
    } catch (error) {
      console.log('Error loading theme preference:', error);
    } finally {
      setIsLoaded(true);
    }
  };

  const setThemeMode = async (mode: ThemeMode) => {
    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, mode);
      setThemeModeState(mode);
    } catch (error) {
      console.log('Error saving theme preference:', error);
    }
  };

  const toggleTheme = () => {
    const newMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
  };

  // Calculate the effective theme based on mode and system preference
  const effectiveTheme = useMemo((): 'light' | 'dark' => {
    if (themeMode === 'system') {
      return systemColorScheme === 'dark' ? 'dark' : 'light';
    }
    return themeMode;
  }, [themeMode, systemColorScheme]);

  const isDark = effectiveTheme === 'dark';
  const colors = isDark ? darkColors : lightColors;

  const theme: Theme = useMemo(() => ({
    mode: effectiveTheme,
    colors,
  }), [effectiveTheme, colors]);

  const contextValue = useMemo(() => ({
    theme,
    themeMode,
    isDark,
    setThemeMode,
    toggleTheme,
    colors,
  }), [theme, themeMode, isDark, colors]);

  // Don't render until theme preference is loaded
  if (!isLoaded) {
    return null;
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================================
// useTheme Hook
// ============================================================

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Export colors for direct access when needed
export { lightColors, darkColors };
